// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material"
import { CreditCard, Plus, Edit, Calendar, Repeat, Clock, Send, Info } from "lucide-react"
import type { DocumentItem } from "./document-list"
import { CustomerInvoicesTable } from "./customer-invoices-table"
// Financial wall: route every money render on this MIXED panel through the
// single choke point so a non-financial viewer sees no dollar figures, while the
// agreement metadata (product / SLA / dealers / renewal) stays visible.
import { Money, FinancialOnly } from "@crm-src/components/money"
import { ResellerOnly } from "@crm-src/components/reseller"

// Re-export DocumentItem as DealInvoice for backward compatibility
export type DealInvoice = DocumentItem

export interface Deal {
  id: string
  name: string
  start_date: string
  end_date?: string
  renewal_date: string
  equipment_price: number
  maintenance_percentage: number
  invoices: DealInvoice[]
}

// minutes → compact human label: 60→"1h", 90→"1h 30m", 480→"8h",
// 2880→"2d". Used for the SLA cell on the Agreement card.
function formatSlaMinutes(min?: number | null): string {
  if (min == null) return "—"
  if (min === 0) return "0m"
  const d = Math.floor(min / 1440)
  const h = Math.floor((min % 1440) / 60)
  const m = min % 60
  return [d ? `${d}d` : "", h ? `${h}h` : "", m ? `${m}m` : ""].filter(Boolean).join(" ") || "0m"
}

// Scheduled invoice type for display
export interface ScheduledInvoiceInfo {
  id: string
  scheduled_date: string
  status: string
  recipient_email?: string
  source_invoice?: {
    id: string
    invoice_number: string
    total_amount: number
  }
  invoice_schedule?: {
    id: string
    name: string
    frequency: string
  }
}

// Recurring schedule type for display
export interface RecurringScheduleInfo {
  id: string
  name: string
  frequency: string
  status: string
  next_scheduled_at?: string
  source_invoice?: {
    id: string
    invoice_number: string
    total_amount: number
  }
}

interface DealsInvoicesPanelProps {
  deals: Deal[]
  customerName?: string
  customerId?: string
  customerEmail?: string
  supportLevel?: string
  // Per-customer SLA targets, mirrored from the CRM customer record.
  // The plan NAME is not shown in the CRM — it is derived from the
  // support level and displayed on the customer portal only.
  slaFirstResponseMinutes?: number | null
  slaResolutionMinutes?: number | null
  reseller?: {
    id: number
    name: string
    code?: string
  } | null
  // Product / feature / version owned by the customer record. Edited
  // in the Agreement modal alongside Reseller (see edit-agreement-modal).
  product?: {
    id: number
    name: string
    code?: string
  } | null
  productFeature?: {
    id: number
    name: string
    code?: string
  } | null
  // Per-product model: the release the customer runs + the selected features
  // (resolved to names by the crm-api serializer from feature_codes). These
  // REPLACE the legacy single productFeature on the card.
  release?: string | null
  features?: { code: string; name: string }[]
  dealers?: Array<{
    id: string
    name: string
    address?: string
  }>
  // Scheduled invoices data
  nextScheduledInvoice?: ScheduledInvoiceInfo | null
  recurringSchedules?: RecurringScheduleInfo[]
  onEditAgreement?: () => void
  onCreateAgreement?: () => void
  // Scheduling callbacks
  onSetupRecurring?: () => void
  onViewScheduledInvoice?: (id: string) => void
  onViewAllSchedules?: () => void
  // Renewal date callback
  onEditRenewalDate?: (dealId: string, currentDate: string | null) => void
  // Send reminder callback
  onSendRenewalReminder?: (dealId: string) => Promise<void>
  isSendingReminder?: boolean
  onRefresh?: () => void
  isLoading?: boolean
  className?: string
  formatCurrency?: (amount: number) => string
}

export function DealsInvoicesPanel({
  deals,
  customerName,
  customerId,
  customerEmail,
  supportLevel,
  slaFirstResponseMinutes,
  slaResolutionMinutes,
  reseller,
  product,
  release,
  features = [],
  dealers = [],
  nextScheduledInvoice,
  recurringSchedules = [],
  onEditAgreement,
  onCreateAgreement,
  onSetupRecurring,
  onViewScheduledInvoice,
  onViewAllSchedules,
  onEditRenewalDate,
  onSendRenewalReminder,
  isSendingReminder = false,
  isLoading = false,
  className = "",
  formatCurrency = (amount) => `$${amount.toLocaleString()}`,
  onRefresh,
}: DealsInvoicesPanelProps) {
  // Invoices + Quotes are now fetched SERVER-SIDE (paginated + sorted) by
  // <CustomerInvoicesTable> scoped to `customerId`, so the panel no longer
  // splits the deal-embedded `invoices` array in the browser.

  // Helper functions for display formatting
  const formatSupportLevel = (level?: string) => {
    if (!level) return 'None'
    switch (level.toLowerCase()) {
      case 'none': return 'None'
      case 'weekdays': return 'Week Days'
      case '24_7': return '24/7'
      default: return level
    }
  }

  const formatReseller = (resellerData?: { name: string, code?: string } | null) => {
    if (!resellerData) return 'Direct'
    return resellerData.name
  }

  // Product + the release the customer runs (per-product model). The purchased
  // FEATURES are a separate multi-value list (shown below) — no longer a single
  // "Feature" segment. Examples: "USN", "USN · v10.1.0".
  const formatProduct = (
    productData?: { name: string } | null,
    releaseVersion?: string | null,
  ) => {
    const parts: string[] = []
    if (productData?.name) parts.push(productData.name)
    if (releaseVersion) parts.push(`v${String(releaseVersion).replace(/^v/i, '')}`)
    return parts.length > 0 ? parts.join(' · ') : 'Not set'
  }

  // Helper function to calculate next invoice date and amount
  const getNextInvoiceInfo = (deal?: Deal): {
    date: string;
    amount: number;
    isOverdue: boolean;
    isUpcoming: boolean;
    description: string;
  } => {
    if (!deal) return {
      date: 'No agreement',
      amount: 0,
      isOverdue: false,
      isUpcoming: false,
      description: 'No agreement'
    }

    return {
      date: 'Not scheduled',
      amount: 0,
      isOverdue: false,
      isUpcoming: false,
      description: 'Not scheduled'
    }
  }

  // Helper function to get maintenance renewal date
  const getMaintenanceRenewalDate = (deal?: Deal): { date: string; isUpcoming: boolean; isPast: boolean; isScheduled: boolean } => {
    if (!deal) return { date: 'No agreement', isUpcoming: false, isPast: false, isScheduled: false }

    if (!deal.renewal_date) {
      return { date: 'Not scheduled', isUpcoming: false, isPast: false, isScheduled: false }
    }

    const renewalDate = new Date(deal.renewal_date)
    const today = new Date()
    const diffTime = renewalDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return {
      date: renewalDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      isUpcoming: diffDays >= 0 && diffDays <= 30,
      isPast: diffDays < 0,
      isScheduled: true
    }
  }

  // Helper function to calculate the next reminder date/time
  // Reminders are sent at thresholds: 30, 14, 7, 3, 1, 0, -7 days before/after renewal at 8am
  const getNextReminderInfo = (deal?: Deal): { date: string; time: string; daysLabel: string } | null => {
    if (!deal?.renewal_date) return null

    const REMINDER_THRESHOLDS = [30, 14, 7, 3, 1, 0, -7]
    const REMINDER_HOUR = 8 // 8am

    const renewalDate = new Date(deal.renewal_date)
    const now = new Date()

    // Calculate days until renewal (negative means overdue)
    const diffTime = renewalDate.getTime() - now.getTime()
    const daysUntilRenewal = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    // Find the next threshold that will trigger a reminder
    // Thresholds are days BEFORE renewal, so we find the largest threshold <= daysUntilRenewal
    // that hasn't been sent today yet
    for (const threshold of REMINDER_THRESHOLDS) {
      // Calculate the reminder date for this threshold
      const reminderDate = new Date(renewalDate)
      reminderDate.setDate(reminderDate.getDate() - threshold)
      reminderDate.setHours(REMINDER_HOUR, 0, 0, 0)

      // Check if this reminder is in the future (or today but not yet sent)
      if (reminderDate > now) {
        const thresholdLabel = threshold > 0
          ? `${threshold} day${threshold !== 1 ? 's' : ''} before`
          : threshold === 0
            ? 'On renewal day'
            : `${Math.abs(threshold)} day${Math.abs(threshold) !== 1 ? 's' : ''} after`

        return {
          date: reminderDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          }),
          time: '8:00 AM',
          daysLabel: thresholdLabel
        }
      }
    }

    // All reminders have been sent (renewal is more than 7 days overdue)
    return null
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {[1, 2, 3].map((i) => (
          <Card key={i} {...(className ? { className } : {})}>
            <CardHeader title={<Skeleton variant="rounded" width={192} height={24} />} />
            <CardContent>
              <Skeleton variant="rounded" height={192} />
            </CardContent>
          </Card>
        ))}
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Three stacked full-width rows: Agreements, Invoices, Quotes. Inside the
          Agreements card the Product stats + Next-Invoice/renewal panels sit
          side by side (see the inner grid below). */}
      {/* Agreements Card - Stats and Renewal Info Only */}
      <Card {...(className ? { className } : {})}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CreditCard size={20} />
                <Typography component="span" sx={{ fontWeight: 600 }}>Agreements</Typography>
              </Box>
              {deals.length > 0 && (
                <Button size="small" variant="outlined" color="inherit" onClick={onEditAgreement} startIcon={<Edit size={16} />}>
                  Edit
                </Button>
              )}
            </Box>
          }
        />
        <CardContent>
          {deals.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}><CreditCard size={48} /></Box>
              <Typography>No agreements created yet</Typography>
              {onCreateAgreement && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button size="small" variant="contained" onClick={onCreateAgreement} startIcon={<Plus size={16} />}>
                    Create Agreement
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            /* Product stats + Next-Invoice/renewal panels side by side (one row on ≥md). */
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
              {/* Agreement Stats */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", bgcolor: "surfaceSoft", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                {/* Product + release span both columns so the "Product · vN.N.N" label has room */}
                <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderBottom: 1, borderColor: "divider", py: 1, px: 2 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>Product</Typography>
                  <Typography component="p" sx={{ fontWeight: 700, fontSize: 14, color: "primary.main", textAlign: "center", wordBreak: "break-word" }}>
                    {formatProduct(product, release)}
                  </Typography>
                </Box>
                {/* Purchased features (per-product model) — the actual multi-select,
                    resolved to names by the crm-api serializer from feature_codes. */}
                <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderBottom: 1, borderColor: "divider", py: 1, px: 2 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>
                    Features{features.length > 0 ? ` (${features.length})` : ""}
                  </Typography>
                  <Typography component="p" sx={{ fontWeight: 600, fontSize: 14, color: features.length > 0 ? "text.primary" : "text.secondary", textAlign: "center", wordBreak: "break-word" }}>
                    {features.length > 0 ? features.map((f) => f.name).join(" · ") : "None selected"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderRight: 1, borderBottom: 1, borderColor: "divider", py: 1 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>Equipment</Typography>
                  {/* Financial wall: equipment price is money → redacts to "—" without access */}
                  <Typography component="p" sx={{ fontWeight: 700, fontSize: 14, color: "primary.main", textAlign: "center" }}><Money value={deals[0]?.equipment_price || 0} options={{ decimals: 0 }} /></Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderBottom: 1, borderColor: "divider", py: 1 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>Maintenance</Typography>
                  <Typography component="p" sx={{ fontWeight: 700, fontSize: 14, color: "warning.main", textAlign: "center" }}>{deals[0]?.maintenance_percentage || 0}%</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderRight: 1, borderColor: "divider", py: 1 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>Support</Typography>
                  <Typography component="p" sx={{ fontWeight: 700, fontSize: 14, color: "success.main", textAlign: "center" }}>{formatSupportLevel(supportLevel)}</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", py: 1 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>Reseller</Typography>
                  {/* Reseller wall: the reseller name is redacted to "—" (the
                      stat tile stays so the bordered grid keeps its shape) for a
                      viewer without crm:resellers. See components/reseller.tsx. */}
                  <Typography component="p" sx={{ fontWeight: 700, fontSize: 14, color: "secondary.main", textAlign: "center" }}><ResellerOnly fallback="—">{formatReseller(reseller)}</ResellerOnly></Typography>
                </Box>
                {/* SLA targets (mirrored from the synced support plan) */}
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderRight: 1, borderTop: 1, borderColor: "divider", py: 1 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>SLA first response</Typography>
                  <Typography component="p" sx={{ fontWeight: 700, fontSize: 14, color: "success.main", textAlign: "center" }}>{formatSlaMinutes(slaFirstResponseMinutes)}</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderTop: 1, borderColor: "divider", py: 1 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>SLA resolution</Typography>
                  <Typography component="p" sx={{ fontWeight: 700, fontSize: 14, color: "success.main", textAlign: "center" }}>{formatSlaMinutes(slaResolutionMinutes)}</Typography>
                </Box>
                <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderTop: 1, borderColor: "divider", py: 1, px: 2 }}>
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 0.25 }}>Licence Ends</Typography>
                  <Typography component="p" sx={{ fontWeight: 700, fontSize: 14, color: "primary.main", textAlign: "center" }}>{deals[0]?.end_date || '—'}</Typography>
                </Box>
                {/* Dealers row - only show if dealers are attached */}
                {dealers && dealers.length > 0 && (
                  <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", borderTop: 1, borderColor: "divider", py: 1, px: 2 }}>
                    <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mb: 1 }}>Dealers</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                      {dealers.map((dealer) => (
                        <Chip key={dealer.id} size="small" color="secondary" variant="outlined" label={dealer.name} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Payment & Renewal Information */}
              {(() => {
                const maintenanceRenewal = getMaintenanceRenewalDate(deals[0])

                // Calculate days until scheduled send
                const getDaysUntil = (dateStr: string) => {
                  const date = new Date(dateStr)
                  const now = new Date()
                  const diffTime = date.getTime() - now.getTime()
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

                  if (diffDays < 0) return { text: 'Overdue', isOverdue: true, isUpcoming: false }
                  if (diffDays === 0) return { text: 'Today', isOverdue: false, isUpcoming: true }
                  if (diffDays === 1) return { text: 'Tomorrow', isOverdue: false, isUpcoming: true }
                  if (diffDays <= 7) return { text: `In ${diffDays} days`, isOverdue: false, isUpcoming: true }
                  return { text: new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), isOverdue: false, isUpcoming: false }
                }

                const hasScheduled = nextScheduledInvoice && nextScheduledInvoice.scheduled_date
                const scheduledInfo = hasScheduled ? getDaysUntil(nextScheduledInvoice.scheduled_date) : null
                const hasRecurring = recurringSchedules && recurringSchedules.length > 0
                const activeRecurring = recurringSchedules?.find(s => s.status === 'active')

                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, bgcolor: "surfaceSoft", border: 1, borderColor: "divider", borderRadius: 1 }}>
                    {/* Next Invoice to Send - Interactive */}
                    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography component="p" sx={{ fontSize: 14, fontWeight: 500, color: "primary.main" }}>Next Scheduled Invoice</Typography>
                        <Tooltip
                          title={
                            <Box sx={{ fontSize: 12 }}>
                              <strong>Recurring:</strong> Automatically generate invoices on a schedule (weekly, monthly, etc.). Each generated invoice is emailed to finance for review — nothing is sent to the customer automatically.
                            </Box>
                          }
                        >
                          <Box component="span" sx={{ display: "flex", color: "primary.light" }}><Info size={14} /></Box>
                        </Tooltip>
                      </Box>

                      {hasScheduled ? (
                        <Box sx={{ width: "100%" }}>
                          <Box
                            sx={{ cursor: "pointer", borderRadius: 1, p: 1, m: -1, transition: "background-color 0.15s", "&:hover": { bgcolor: "action.hover" } }}
                            onClick={() => onViewScheduledInvoice?.(nextScheduledInvoice.id)}
                          >
                            {/* Date with recurring indicator */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                              <Box component="span" sx={{ display: "flex", color: "primary.main" }}><Clock size={16} /></Box>
                              <Typography component="p" sx={{ fontSize: 14, fontWeight: 600, color: scheduledInfo?.isOverdue ? "error.dark" : scheduledInfo?.isUpcoming ? "warning.dark" : "text.primary" }}>
                                {scheduledInfo?.text}
                              </Typography>
                              {nextScheduledInvoice.invoice_schedule && (
                                <Typography component="span" sx={{ fontSize: 12, color: "secondary.main", fontWeight: 500 }}>
                                  ({nextScheduledInvoice.invoice_schedule.frequency})
                                </Typography>
                              )}
                              {scheduledInfo?.isOverdue && (
                                <Chip size="small" color="error" label="OVERDUE" />
                              )}
                              {scheduledInfo?.isUpcoming && !scheduledInfo?.isOverdue && (
                                <Chip size="small" color="warning" label="SOON" />
                              )}
                            </Box>
                            {nextScheduledInvoice.source_invoice && (
                              <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                                {nextScheduledInvoice.source_invoice.invoice_number}
                                {/* Financial wall: the scheduled invoice amount is money → hidden without access */}
                                <FinancialOnly>
                                  {" • "}
                                  <Money value={nextScheduledInvoice.source_invoice.total_amount || 0} />
                                </FinancialOnly>
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography component="p" sx={{ fontSize: 14, color: "text.secondary" }}>Not scheduled</Typography>
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              onClick={onSetupRecurring}
                              startIcon={<Repeat size={12} />}
                              sx={{ fontSize: 12 }}
                            >
                              Recurring
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Divider */}
                    <Box sx={{ borderTop: 1, borderColor: "divider" }} />

                    {/* AMC Renewal */}
                    {(() => {
                      const nextReminder = getNextReminderInfo(deals[0])

                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Typography component="p" sx={{ fontSize: 14, fontWeight: 500, color: "primary.main" }}>AMC Renewal Reminder</Typography>
                            <Tooltip
                              title={
                                <Box sx={{ fontSize: 12 }}>
                                  <strong>AMC (Annual Maintenance Contract):</strong> The date when the maintenance/support contract needs to be renewed.<br/><br/>
                                  A reminder task will be created on calendar and email notifications will be sent before the renewal date.
                                </Box>
                              }
                            >
                              <Box component="span" sx={{ display: "flex", color: "primary.light" }}><Info size={14} /></Box>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                            <Typography
                              component="p"
                              sx={{ fontSize: "15px", fontWeight: 500, color: !maintenanceRenewal.isScheduled ? "text.secondary" : maintenanceRenewal.isPast ? "error.dark" : maintenanceRenewal.isUpcoming ? "warning.dark" : "text.primary" }}
                            >
                              {maintenanceRenewal.date}
                            </Typography>
                            {maintenanceRenewal.isScheduled && maintenanceRenewal.isPast && (
                              <Chip size="small" color="error" label="EXPIRED" />
                            )}
                            {maintenanceRenewal.isScheduled && maintenanceRenewal.isUpcoming && !maintenanceRenewal.isPast && (
                              <Chip size="small" color="warning" label="RENEWING SOON" />
                            )}
                          </Box>
                          {/* Next Reminder Info */}
                          {maintenanceRenewal.isScheduled && nextReminder && (
                            <Box sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                <Send size={12} />
                                <Box component="span">Next reminder: <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>{nextReminder.date}</Box> at {nextReminder.time}</Box>
                              </Box>
                              <Typography component="p" sx={{ color: "text.disabled", mt: 0.25 }}>({nextReminder.daysLabel})</Typography>
                            </Box>
                          )}
                          {maintenanceRenewal.isScheduled && !nextReminder && maintenanceRenewal.isPast && (
                            <Typography component="p" sx={{ mt: 1, fontSize: 12, color: "text.disabled" }}>All reminders sent</Typography>
                          )}
                          {deals[0] && (
                            maintenanceRenewal.isScheduled ? (
                              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                {onEditRenewalDate && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => onEditRenewalDate(deals[0].id, deals[0].renewal_date || null)}
                                    startIcon={<Edit size={12} />}
                                    sx={{ fontSize: 12 }}
                                  >
                                    Edit Date
                                  </Button>
                                )}
                                {onSendRenewalReminder && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => onSendRenewalReminder(deals[0].id)}
                                    disabled={isSendingReminder}
                                    startIcon={<Send size={12} />}
                                    sx={{ fontSize: 12 }}
                                  >
                                    {isSendingReminder ? 'Sending...' : 'Send Now'}
                                  </Button>
                                )}
                              </Box>
                            ) : onEditRenewalDate ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => onEditRenewalDate(deals[0].id, deals[0].renewal_date || null)}
                                startIcon={<Calendar size={12} />}
                                sx={{ fontSize: 12 }}
                              >
                                Set Date
                              </Button>
                            ) : null
                          )}
                        </Box>
                      )
                    })()}
                  </Box>
                )
              })()}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Invoices Card - modern server-side paginated + sortable table */}
      {/* Financial wall: invoices (numbers + amounts + invoice actions) are
          inherently financial → hide the entire list without access. The
          Agreement card above stays, so the panel doesn't go blank. */}
      {customerId && (
        <FinancialOnly>
          <CustomerInvoicesTable
            customerId={customerId}
            documentType="invoice"
            {...(onRefresh ? { onChanged: onRefresh } : {})}
            formatCurrency={formatCurrency}
            className={className}
            recurringInvoiceIds={recurringSchedules
              ?.filter(s => s.status === 'active' || s.status === 'paused')
              .map(s => s.source_invoice?.id?.toString())
              .filter((id): id is string => !!id) || []}
          />
        </FinancialOnly>
      )}

      {/* Quotes Card - modern server-side paginated + sortable table */}
      {/* Financial wall: quotes are priced documents → hidden without access. */}
      {customerId && (
        <FinancialOnly>
          <CustomerInvoicesTable
            customerId={customerId}
            documentType="quote"
            {...(onRefresh ? { onChanged: onRefresh } : {})}
            formatCurrency={formatCurrency}
            className={className}
          />
        </FinancialOnly>
      )}
    </Box>
  )
}
